<template>
  <!-- Mobile Slide-up Modal -->
  <div v-if="isMobile && isVisible" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
    <div class="w-full bg-blue-100 rounded-t-3xl transform transition-transform duration-300 ease-out max-h-[90vh] overflow-hidden"
         :class="isVisible ? 'translate-y-0' : 'translate-y-full'">
      <!-- Close Button -->
      <button
        @click="close"
        class="absolute top-4 right-4 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all z-10"
      >
        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>

      <!-- Mobile Content -->
      <div class="h-full overflow-y-auto pt-4">
        <!-- Loading state -->
        <div v-if="isLoading" class="px-6 py-6 flex flex-col items-center justify-center min-h-[300px]">
          <svg class="animate-spin h-10 w-10 text-teal-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-gray-600">Getting weather information...</p>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="px-6 py-6">
          <div class="bg-red-100 text-teal-500 p-4 rounded-xl">
            <p class="font-medium">{{ error }}</p>
            <button @click="fetchWeatherData" class="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
              Retry
            </button>
          </div>
        </div>

        <!-- Weather content -->
        <div v-else-if="weatherData" class="px-6 py-6 pb-8">
        <!-- Header with location search -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-yellow-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd" />
              </svg>
              <h2 class="text-xl font-bold text-gray-800">Weather</h2>
            </div>
            <div class="text-gray-500 text-sm">
              {{ weatherData.locationNormalized || 'Current Location' }}
            </div>
          </div>

          <!-- Location Search Input -->
          <div class="relative">
            <div class="relative">
              <input
                v-model="searchQuery"
                @input="handleSearchInput"
                @focus="showSuggestions = true"
                @blur="handleBlur"
                type="text"
                placeholder="Search for a location..."
                class="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <!-- Autocomplete Suggestions -->
            <div
              v-if="showSuggestions && suggestions.length > 0"
              class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 mt-1 max-h-48 overflow-y-auto"
            >
              <div
                v-for="(suggestion, index) in suggestions"
                :key="index"
                @mousedown="selectSuggestion(suggestion)"
                class="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div class="font-medium text-gray-800">{{ suggestion.name }}</div>
                <div class="text-sm text-gray-500">{{ suggestion.country }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Current weather -->
        <div v-if="weatherData.current && weatherData.current.main && weatherData.current.weather" class="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl text-white p-6 mb-6">
          <div class="flex items-center justify-between">
            <div>
              <div class="text-4xl font-bold">{{ Math.round(weatherData.current.main.temp) }}°C</div>
              <div class="text-lg capitalize">{{ weatherData.current.weather[0].description }}</div>
              <div class="text-sm opacity-80 mt-2">Feels like {{ Math.round(weatherData.current.main.feels_like) }}°C</div>
            </div>
            <div>
              <img
                :src="`https://openweathermap.org/img/wn/${weatherData.current.weather[0].icon}@2x.png`"
                :alt="weatherData.current.weather[0].description"
                class="w-24 h-24"
              />
            </div>
          </div>

          <!-- Weather details -->
          <div class="grid grid-cols-2 gap-4 mt-2 pt-2 border-t border-white border-opacity-20">
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 opacity-80" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
              </svg>
              <div>
                <div class="text-sm opacity-80">Humidity</div>
                <div>{{ weatherData.current.main.humidity }}%</div>
              </div>
            </div>
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 opacity-80" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
              </svg>
              <div>
                <div class="text-sm opacity-80">Pressure</div>
                <div>{{ weatherData.current.main.pressure }} hPa</div>
              </div>
            </div>
            <div class="flex items-center" v-if="weatherData.current.wind">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 opacity-80" viewBox="0 0 20 20" fill="currentColor">
                <path d="M5.5 16a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 16h-8z" />
              </svg>
              <div>
                <div class="text-sm opacity-80">Wind</div>
                <div>{{ Math.round(weatherData.current.wind.speed * 3.6) }} km/h</div>
              </div>
            </div>
            <div class="flex items-center" v-if="weatherData.current.visibility">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 opacity-80" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
              </svg>
              <div>
                <div class="text-sm opacity-80">Visibility</div>
                <div>{{ (weatherData.current.visibility / 1000).toFixed(1) }} km</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Weather data not available -->
        <div v-else-if="!isLoading && !error" class="text-center py-8">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
          <p class="text-gray-600 mb-4">Weather data not available</p>
          <button @click="fetchWeatherData" class="bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-yellow-600">
            Try Again
          </button>
        </div>

        <!-- Forecast -->
        <div v-if="weatherData.forecast && weatherData.forecast.list">
          <h3 class="text-lg font-semibold text-gray-800 mb-2">5-Day Forecast</h3>
          <div class="overflow-x-auto hide-scrollbar">
            <div class="flex space-x-4 pb-2">
              <div
                v-for="(item, index) in forecastItems"
                :key="index"
                class="flex-shrink-0 w-24 bg-gray-100 rounded-lg p-3 text-center"
              >
                <div class="text-sm text-gray-500">{{ formatForecastDate(item.dt) }}</div>
                <img
                  :src="`https://openweathermap.org/img/wn/${item.weather[0].icon}.png`"
                  :alt="item.weather[0].description"
                  class="w-10 h-10 mx-auto"
                />
                <div class="font-medium">{{ Math.round(item.main.temp) }}°C</div>
                <div class="text-xs text-gray-500 capitalize">{{ item.weather[0].description }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>

  <!-- Desktop/Inline Mode -->
  <div v-else-if="isInline || (!isMobile && isVisible)"
       class="h-full bg-blue-100 rounded-3xl overflow-hidden">
    <!-- Desktop Content -->
    <div class="h-full overflow-y-auto pt-4">
      <!-- Loading state -->
      <div v-if="isLoading" class="px-6 py-6 flex flex-col items-center justify-center min-h-[300px]">
        <svg class="animate-spin h-10 w-10 text-teal-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-gray-600">Getting weather information...</p>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="px-6 py-6">
        <div class="bg-red-100 text-teal-500 p-4 rounded-xl">
          <p class="font-medium">{{ error }}</p>
          <button @click="loadUserLocationWeather" class="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
            Retry
          </button>
        </div>
      </div>

      <!-- Weather content -->
      <div v-else-if="weatherData" class="px-6 py-6 pb-8">
        <!-- Header with location search -->
        <div class="mb-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-yellow-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd" />
              </svg>
              <h2 class="text-xl font-bold text-gray-800">Weather</h2>
            </div>
            <div class="text-gray-500 text-sm">
              {{ weatherData.locationNormalized || 'Current Location' }}
            </div>
          </div>

          <!-- Location Search Input -->
          <div class="relative">
            <div class="relative">
              <input
                v-model="searchQuery"
                @input="handleSearchInput"
                @focus="showSuggestions = true"
                @blur="handleBlur"
                type="text"
                placeholder="Search for a location..."
                class="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <!-- Autocomplete Suggestions -->
            <div
              v-if="showSuggestions && suggestions.length > 0"
              class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 mt-1 max-h-48 overflow-y-auto"
            >
              <div
                v-for="(suggestion, index) in suggestions"
                :key="index"
                @mousedown="selectSuggestion(suggestion)"
                class="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div class="font-medium text-gray-800">{{ suggestion.name }}</div>
                <div class="text-sm text-gray-500">{{ suggestion.country }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Current weather -->
        <div v-if="weatherData.current && weatherData.current.main && weatherData.current.weather" class="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl text-white p-6 mb-6">
          <div class="flex items-center justify-between">
            <div>
              <div class="text-4xl font-bold">{{ Math.round(weatherData.current.main.temp) }}°C</div>
              <div class="text-lg capitalize">{{ weatherData.current.weather[0].description }}</div>
              <div class="text-sm opacity-80 mt-2">Feels like {{ Math.round(weatherData.current.main.feels_like) }}°C</div>
            </div>
            <div>
              <img
                :src="`https://openweathermap.org/img/wn/${weatherData.current.weather[0].icon}@2x.png`"
                :alt="weatherData.current.weather[0].description"
                class="w-24 h-24"
              />
            </div>
          </div>

          <!-- Weather details -->
          <div class="grid grid-cols-2 gap-4 mt-2 pt-2 border-t border-white border-opacity-20">
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 opacity-80" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
              </svg>
              <div>
                <div class="text-sm opacity-80">Humidity</div>
                <div>{{ weatherData.current.main.humidity }}%</div>
              </div>
            </div>
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 opacity-80" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
              </svg>
              <div>
                <div class="text-sm opacity-80">Pressure</div>
                <div>{{ weatherData.current.main.pressure }} hPa</div>
              </div>
            </div>
            <div class="flex items-center" v-if="weatherData.current.wind">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 opacity-80" viewBox="0 0 20 20" fill="currentColor">
                <path d="M5.5 16a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 16h-8z" />
              </svg>
              <div>
                <div class="text-sm opacity-80">Wind</div>
                <div>{{ Math.round(weatherData.current.wind.speed * 3.6) }} km/h</div>
              </div>
            </div>
            <div class="flex items-center" v-if="weatherData.current.visibility">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 opacity-80" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
              </svg>
              <div>
                <div class="text-sm opacity-80">Visibility</div>
                <div>{{ (weatherData.current.visibility / 1000).toFixed(1) }} km</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Weather data not available -->
        <div v-else-if="!isLoading && !error" class="text-center py-8">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
          <p class="text-gray-600 mb-4">Weather data not available</p>
          <button @click="loadUserLocationWeather" class="bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-yellow-600">
            Try Again
          </button>
        </div>

        <!-- Forecast -->
        <div v-if="weatherData.forecast && weatherData.forecast.list">
          <h3 class="text-lg font-semibold text-gray-800 mb-2">5-Day Forecast</h3>
          <div class="overflow-x-auto hide-scrollbar">
            <div class="flex space-x-4 pb-2">
              <div
                v-for="(item, index) in forecastItems"
                :key="index"
                class="flex-shrink-0 w-24 bg-gray-100 rounded-lg p-3 text-center"
              >
                <div class="text-sm text-gray-500">{{ formatForecastDate(item.dt) }}</div>
                <img
                  :src="`https://openweathermap.org/img/wn/${item.weather[0].icon}.png`"
                  :alt="item.weather[0].description"
                  class="w-10 h-10 mx-auto"
                />
                <div class="font-medium">{{ Math.round(item.main.temp) }}°C</div>
                <div class="text-xs text-gray-500 capitalize">{{ item.weather[0].description }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCurrentLocation, fetchCurrentWeather, fetchWeatherForecast } from '@/services/weatherService';
import { validateCoordinates } from '@/utils/geolocation';

export default {
  name: 'WeatherPopup',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    isInline: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  data() {
    return {
      isLoading: false,
      error: null,
      weatherData: null,
      coordinates: null,
      searchQuery: '',
      suggestions: [],
      showSuggestions: false,
      searchTimeout: null,
      isSearchMode: false,
      userLocationWeatherData: null
    }
  },
  computed: {
    forecastItems() {
      if (!this.weatherData || !this.weatherData.forecast || !this.weatherData.forecast.list) {
        return [];
      }

      // Get one forecast per day (at noon)
      const dailyForecasts = [];
      const processedDates = new Set();

      this.weatherData.forecast.list.forEach(item => {
        const date = new Date(item.dt * 1000).toDateString();

        if (!processedDates.has(date)) {
          processedDates.add(date);
          dailyForecasts.push(item);
        }
      });

      return dailyForecasts.slice(0, 5); // Limit to 5 days
    }
  },
  mounted() {
    // If component is mounted in inline mode, load weather data immediately
    if (this.isInline) {
      this.loadUserLocationWeather();
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal && !this.isInline) {
        // Always show user location weather when popup opens (but not for inline mode)
        this.loadUserLocationWeather();
      } else if (!newVal && !this.isInline) {
        // Reset state when popup closes (but not for inline mode)
        this.resetPopupState();
      }
    },
    isInline(newVal) {
      // If component becomes inline, load weather data
      if (newVal) {
        this.loadUserLocationWeather();
      }
    }
  },
  methods: {
    close() {
      if (!this.isInline) {
        this.$emit('close');
      }
    },
    resetPopupState() {
      // Clear search input and reset to user location
      this.searchQuery = '';
      this.suggestions = [];
      this.showSuggestions = false;
      this.isSearchMode = false;

      // Clear search timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
    },
    async loadUserLocationWeather() {
      // Always load user location weather when popup opens
      this.isSearchMode = false;

      try {
        // Check for cached user location data first
        const cachedData = this.getCachedUserLocationWeatherData();
        if (cachedData) {
          this.weatherData = cachedData;
          this.userLocationWeatherData = cachedData;
          return;
        }

        // Fetch fresh user location weather data
        await this.fetchUserLocationWeatherData();
      } catch (error) {
        this.error = error.message || 'Failed to load weather data';
      }
    },
    async fetchUserLocationWeatherData() {
      this.isLoading = true;
      this.error = null;

      try {
        // Get current user location
        const coordinates = await getCurrentLocation();

        // Validate coordinates
        if (!validateCoordinates(coordinates)) {
          throw new Error('Invalid location coordinates');
        }

        // Fetch weather data
        const [current, forecast] = await Promise.all([
          fetchCurrentWeather(coordinates.lat, coordinates.lon),
          fetchWeatherForecast(coordinates.lat, coordinates.lon)
        ]);

        // Validate API responses
        if (!current || !forecast) {
          throw new Error('Invalid response from weather service');
        }

        // Set weather data
        const locationString = current.name ? `${current.name}, ${current.sys.country}` : 'Current Location';

        const weatherData = {
          current,
          forecast,
          location: locationString,
          locationNormalized: this.normalizeLocationName(locationString),
          coordinates,
          timestamp: new Date().toISOString(),
          isUserLocation: true
        };

        this.weatherData = weatherData;
        this.userLocationWeatherData = weatherData;
        this.coordinates = coordinates;

        // Cache user location weather data
        this.cacheUserLocationWeatherData(weatherData);
      } catch (error) {
        this.error = error.message || 'Failed to fetch weather data. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async handleSearchInput() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Debounce the search
      this.searchTimeout = setTimeout(async () => {
        if (this.searchQuery.length >= 2) {
          await this.searchLocations();
        } else {
          this.suggestions = [];
          this.showSuggestions = false;
        }
      }, 300);
    },
    async searchLocations() {
      try {
        // Import the API key
        const { key_ } = await import('@/config/api');

        // Use OpenWeatherMap Geocoding API for location suggestions
        const response = await fetch(
          `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(this.searchQuery)}&limit=3&appid=${key_}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch location suggestions');
        }

        const locations = await response.json();
        this.suggestions = locations.map(location => ({
          name: location.name,
          country: location.country,
          state: location.state,
          lat: location.lat,
          lon: location.lon
        }));
        this.showSuggestions = true;
      } catch (error) {
        this.suggestions = [];
        this.showSuggestions = false;
      }
    },
    selectSuggestion(suggestion) {
      this.searchQuery = `${suggestion.name}, ${suggestion.country}`;
      this.showSuggestions = false;
      this.suggestions = [];

      // Fetch weather for selected location (no caching for search results)
      this.fetchSearchLocationWeatherData({
        lat: suggestion.lat,
        lon: suggestion.lon,
        locationName: `${suggestion.name}, ${suggestion.country}`
      });
    },
    async fetchSearchLocationWeatherData(locationData) {
      this.isLoading = true;
      this.error = null;
      this.isSearchMode = true;

      try {
        // Validate coordinates
        if (!validateCoordinates(locationData)) {
          throw new Error('Invalid location coordinates');
        }

        // Fetch weather data (always fresh for search results)
        const [current, forecast] = await Promise.all([
          fetchCurrentWeather(locationData.lat, locationData.lon),
          fetchWeatherForecast(locationData.lat, locationData.lon)
        ]);

        // Validate API responses
        if (!current || !forecast) {
          throw new Error('Invalid response from weather service');
        }

        // Set weather data for searched location
        const locationString = locationData.locationName ||
          (current.name ? `${current.name}, ${current.sys.country}` : 'Unknown Location');

        this.weatherData = {
          current,
          forecast,
          location: locationString,
          locationNormalized: this.normalizeLocationName(locationString),
          coordinates: {
            lat: locationData.lat,
            lon: locationData.lon
          },
          timestamp: new Date().toISOString(),
          isUserLocation: false,
          isSearchResult: true
        };

        this.coordinates = {
          lat: locationData.lat,
          lon: locationData.lon
        };

        // Note: We don't cache search results as per requirements
      } catch (error) {
        this.error = error.message || 'Failed to fetch weather data for this location.';
      } finally {
        this.isLoading = false;
      }
    },
    handleBlur() {
      // Delay hiding suggestions to allow for click events
      setTimeout(() => {
        this.showSuggestions = false;
      }, 200);
    },
    getCachedUserLocationWeatherData() {
      try {
        const cached = localStorage.getItem('travley_user_weather_cache');
        if (!cached) return null;

        const data = JSON.parse(cached);

        // Only return cached data if it's for user location
        if (data.cacheType === 'user_location' && data.isUserLocation) {
          return {
            current: data.current,
            forecast: data.forecast,
            location: data.location,
            locationNormalized: data.locationNormalized,
            coordinates: data.coordinates,
            timestamp: data.timestamp,
            isUserLocation: true
          };
        }

        return null;
      } catch (error) {
        // If there's an error parsing cached data, remove it
        localStorage.removeItem('travley_user_weather_cache');
        return null;
      }
    },
    cacheUserLocationWeatherData(weatherData) {
      try {
        // Only cache user location weather data
        if (!weatherData.isUserLocation) {
          return;
        }

        const cacheData = {
          current: weatherData.current,
          forecast: weatherData.forecast,
          location: weatherData.location,
          locationNormalized: weatherData.locationNormalized,
          coordinates: weatherData.coordinates,
          timestamp: weatherData.timestamp,
          isUserLocation: true,
          cacheType: 'user_location'
        };

        localStorage.setItem('travley_user_weather_cache', JSON.stringify(cacheData));
      } catch (error) {
        // Error caching data - continue silently
      }
    },
    formatForecastDate(timestamp) {
      const date = new Date(timestamp * 1000);
      return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    },
    normalizeLocationName(name) {
      if (!name) return 'Current Location';

      // Remove diacritical marks (accents, macrons, etc.)
      const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      // Extract city name (remove country code if present)
      const parts = normalized.split(',');
      const cityName = parts[0].trim();

      return cityName;
    }
  }
}
</script>

<style scoped>
/* Hide scrollbar while maintaining scroll functionality */
.hide-scrollbar {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.hide-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}
</style>
